import { Component } from '@theme/component';

/**
 * About page "How to play": the STEP buttons bring their card to the front of the stack.
 * Cards get data-pos 0 (front), 1 and 2 (behind); CSS places and tilts them.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [cards] - The step cards, in order.
 * @property {HTMLButtonElement[]} [tabs] - The STEP buttons, in the same order.
 *
 * @extends {Component<Refs>}
 */
class GjHowToPlayComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#show(0, false);
  }

  /**
   * Called by on:click="/select/<index>" on each STEP button.
   * @param {number} index
   */
  select(index) {
    this.#show(Number(index) || 0, true);
  }

  /**
   * Arrow keys move between the STEP buttons (tab pattern).
   * @param {KeyboardEvent} event
   */
  onTabKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (current === -1) return;

    let next = current;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    this.#show(next, true);
    tabs[next]?.focus();
  }

  /**
   * @param {number} active
   * @param {boolean} animate
   */
  #show(active, animate) {
    const cards = this.refs.cards ?? [];
    const tabs = this.refs.tabs ?? [];
    const count = cards.length;
    if (!count) return;

    this.toggleAttribute('data-animate', animate);

    cards.forEach((card, i) => {
      const pos = (i - active + count) % count;
      card.dataset.pos = String(pos);
      card.toggleAttribute('inert', pos !== 0);
      card.setAttribute('aria-hidden', String(pos !== 0));
    });

    tabs.forEach((tab, i) => {
      const selected = i === active;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
  }
}

if (!customElements.get('gj-how-to-play-component')) {
  customElements.define('gj-how-to-play-component', GjHowToPlayComponent);
}
